// Arbre de navigation de l'OAD, source unique de vérité.
//
// Trois niveaux :
//   1. Menu_OAD ligne 1  -> les clés de OAD_NAV
//   2. Menu_OAD ligne 2  -> children du niveau 1
//   3. Side_bar_parametres -> sidebar du niveau 2 (boutons à icône)
//
// MenuOAD et SidebarParametres lisent cet objet : ajouter une page = ajouter
// une entrée ici + le composant dans App.js, rien d'autre à câbler.
//
// `default` = sous-onglet ouvert quand on clique sur le parent.

export const OAD_ROOT = '/oad';

export const OAD_NAV = {
  '2035-2050': {
    label: '2035 - 2050',
    default: 'impacts-globaux',
    children: {
      'impacts-globaux': {
        label: 'Impacts globaux',
        sidebar: null,
      },
      'eau-irrigation': {
        label: 'Eau et irrigation',
        default: 'besoin-en-eau',
        sidebar: [
          { key: 'besoin-en-eau', label: 'Besoin en eau', icon: 'icon_besoin_en_eau' },
          { key: 'tension', label: 'Tension', icon: 'icon_tension' },
          { key: 'rentabilite-irrigation', label: 'Rentabilité irrigation', icon: 'icon_irrig' },
        ],
      },
      adaptations: {
        label: 'Adaptations',
        default: 'classement',
        sidebar: [
          { key: 'classement', label: 'Classement solutions', icon: 'icon_classement' },
          { key: 'changement-semis-variete', label: 'Changement date de semis / variété', icon: 'icon_chgt_semis_variete' },
          { key: 'substitution', label: 'Substitution', icon: 'icon_substitution' },
          { key: 'itk', label: 'Modification ITK', icon: 'icon_itk' },
        ],
      },
    },
  },

  // R3 : Comprendre est listé en premier ET sert de défaut au niveau 1.
  'comparer-comprendre': {
    label: 'Comparer - Comprendre',
    default: 'comprendre',
    children: {
      comprendre: {
        label: 'Comprendre',
        default: 'pheno',
        sidebar: [
          { key: 'pheno', label: 'Phénologie', icon: 'icon_pheno' },
          { key: 'frequence-aleas', label: 'Fréquence aléas', icon: 'icon_freq_aleas' },
        ],
      },
      comparer: {
        label: 'Comparer',
        default: 'risque-actuel',
        sidebar: [
          { key: 'risque-actuel', label: 'Risque actuel', icon: 'icon_risk_actuel' },
          { key: 'frequence-aleas', label: 'Fréquence aléas', icon: 'icon_freq_aleas' },
          { key: 'eau', label: 'Eau', icon: 'icon_besoin_en_eau' },
          { key: 'pheno', label: 'Phénologie', icon: 'icon_pheno' },
        ],
      },
    },
  },

  '2026-2027': {
    label: '2026 - 2027',
    default: 'eau-3-mois',
    children: {
      'eau-3-mois': {
        label: 'Eau à 3 mois',
        default: 'unitaire',
        sidebar: [
          { key: 'unitaire', label: 'Unitaire', icon: 'icon_unitaire' },
          { key: 'territoire', label: 'Total territoire', icon: 'icon_DR_territoire50' },
        ],
      },
      'optimisation-assurance': {
        label: 'Optimisation assurance',
        sidebar: null,
      },
      'optimisation-assolement': {
        label: 'Optimisation assolement',
        sidebar: null,
      },
    },
  },
};

// Le Tableau de bord est un niveau 1 à part : il n'a ni ligne 2 ni sidebar,
// et sert de point d'entrée depuis le bouton "Consulter" d'une simulation.
export const TABLEAU_DE_BORD = {
  key: 'tableau-de-bord',
  label: 'Tableau de bord',
  path: `${OAD_ROOT}/tableau-de-bord`,
};

/**
 * Construit l'URL d'une page OAD.
 * Les niveaux omis sont remplacés par le `default` du niveau parent, ce qui
 * permet d'écrire buildPath('2035-2050') et d'atterrir sur Impacts globaux.
 */
export function buildPath(niveau1, niveau2, niveau3) {
  const n1 = OAD_NAV[niveau1];
  if (!n1) return TABLEAU_DE_BORD.path;

  const k2 = niveau2 || n1.default;
  const n2 = n1.children?.[k2];
  if (!n2) return `${OAD_ROOT}/${niveau1}`;

  // Pas de niveau 3 : l'URL s'arrête au niveau 2 (ex. impacts-globaux).
  if (!n2.sidebar) return `${OAD_ROOT}/${niveau1}/${k2}`;

  const k3 = niveau3 || n2.default;
  return `${OAD_ROOT}/${niveau1}/${k2}/${k3}`;
}

/** Liste des entrées de la ligne 1, dans l'ordre d'affichage. */
export function getNiveau1() {
  return Object.entries(OAD_NAV).map(([key, node]) => ({ key, label: node.label }));
}

/** Liste des entrées de la ligne 2 pour un niveau 1 donné. */
export function getNiveau2(niveau1) {
  const n1 = OAD_NAV[niveau1];
  if (!n1) return [];
  return Object.entries(n1.children).map(([key, node]) => ({ key, label: node.label }));
}

/** Entrées de la sidebar (niveau 3) pour un couple niveau1/niveau2. */
export function getSidebar(niveau1, niveau2) {
  return OAD_NAV[niveau1]?.children?.[niveau2]?.sidebar ?? null;
}
